import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { useAuthStore } from './store/authStore';
import { useI18nStore } from './store/i18nStore';
import LoadingScreen from './components/LoadingScreen';

// Lazy load pages for better performance
const Landing = lazy(() => import('./pages/Landing'));
const Auth = lazy(() => import('./pages/Auth'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Session = lazy(() => import('./pages/Session'));
const Quiz = lazy(() => import('./pages/Quiz'));
const TeachBack = lazy(() => import('./pages/TeachBack'));
const Recall = lazy(() => import('./pages/Recall'));
const Progress = lazy(() => import('./pages/Progress'));
const Library = lazy(() => import('./pages/Library'));
const ConceptView = lazy(() => import('./pages/ConceptView'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Achievements = lazy(() => import('./pages/Achievements'));
const Settings = lazy(() => import('./pages/Settings'));
const Premium = lazy(() => import('./pages/Premium'));
const Pricing = lazy(() => import('./pages/Pricing'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const Certificate = lazy(() => import('./pages/Certificate'));
const SimoExchange = lazy(() => import('./pages/SimoExchange'));
const Challenge = lazy(() => import('./pages/Challenge'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const ParentDashboard = lazy(() => import('./pages/ParentDashboard'));
const ParentBank = lazy(() => import('./pages/ParentBank'));
const ParentCommandCenter = lazy(() => import('./pages/ParentCommandCenter'));
const ProtocolsPage = lazy(() => import('./pages/ProtocolsPage'));
const Lab = lazy(() => import('./pages/Lab'));
const Messages = lazy(() => import('./pages/Messages'));
const CerebralVoyage = lazy(() => import('./pages/CerebralVoyage'));
const ChildRewards = lazy(() => import('./pages/ChildRewards'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Referral = lazy(() => import('./pages/Referral'));

// Protected route wrapper with robust loading handling
const ProtectedRoute = ({ children }) => {
  const { token, user, _hasHydrated, loadUser, loading, logout } = useAuthStore();
  const [authChecked, setAuthChecked] = React.useState(false);
  const [loadAttempts, setLoadAttempts] = React.useState(0);
  
  React.useEffect(() => {
    const checkAuth = async () => {
      if (_hasHydrated && token && !user && loadAttempts < 2) {
        try {
          await loadUser();
        } catch (err) {
          console.error('Failed to load user:', err);
        }
        setLoadAttempts(prev => prev + 1);
      }
      setAuthChecked(true);
    };
    
    if (_hasHydrated) {
      checkAuth();
    }
  }, [_hasHydrated, token, user, loadUser, loadAttempts]);
  
  // Wait for hydration (brief moment on app start)
  if (!_hasHydrated) {
    return <LoadingScreen />;
  }
  
  // No token = not logged in - redirect immediately
  if (!token) {
    return <Navigate to="/auth" replace />;
  }
  
  // Wait for initial auth check (max 1 cycle)
  if (!authChecked && loading) {
    return <LoadingScreen />;
  }
  
  // Token exists but user failed to load after attempts - token is invalid
  if (!user && loadAttempts >= 2) {
    // Clear invalid token and redirect to auth
    logout();
    return <Navigate to="/auth" replace />;
  }
  
  // Still loading user (first attempt)
  if (!user && loading) {
    return <LoadingScreen />;
  }
  
  // User not yet loaded but not loading - trigger load
  if (!user && !loading && loadAttempts < 2) {
    return <LoadingScreen />;
  }
  
  // User loaded but needs onboarding
  if (user && !user.onboarding_complete) {
    return <Navigate to="/onboarding" replace />;
  }
  
  // User fully loaded and onboarded
  if (user && user.onboarding_complete) {
    return children;
  }
  
  // Fallback - should not reach here, but redirect to auth if so
  return <Navigate to="/auth" replace />;
};

// Auth route (redirect if already logged in)
const AuthRoute = ({ children }) => {
  const { token, user, _hasHydrated, loadUser } = useAuthStore();
  const [checked, setChecked] = React.useState(false);
  
  React.useEffect(() => {
    const check = async () => {
      // If we have a token but no user, try to load user
      if (_hasHydrated && token && !user) {
        try {
          await loadUser();
        } catch (err) {
          console.error('AuthRoute: Failed to load user', err);
        }
      }
      setChecked(true);
    };
    
    if (_hasHydrated) {
      check();
    }
  }, [_hasHydrated, token, user, loadUser]);
  
  // Wait for store to hydrate
  if (!_hasHydrated || !checked) {
    return <LoadingScreen />;
  }
  
  // Already logged in with completed onboarding
  if (token && user?.onboarding_complete) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Has token but needs onboarding
  if (token && user && !user.onboarding_complete) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

// Onboarding route (requires token but not completed onboarding)
const OnboardingRoute = ({ children }) => {
  const { token, user, _hasHydrated } = useAuthStore();
  
  // Wait for store to hydrate
  if (!_hasHydrated) {
    return <LoadingScreen />;
  }
  
  // If no token, redirect to auth
  if (!token) {
    return <Navigate to="/auth" replace />;
  }
  
  // If already completed onboarding, redirect to dashboard
  if (user?.onboarding_complete) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// /dashboard router: parent role → Command Center (ResearchLayout). Child role → gamified Dashboard.
// Solo defaults to Command Center but can switch via ?view=child.
// Uses useSearchParams so the layout switches IMMEDIATELY when the query string changes.
const DashboardRouter = () => {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const forcedView = searchParams.get('view');
  const role = user?.user_role || 'solo';
  const inParentMode = (role === 'parent' || role === 'solo') && forcedView !== 'child';
  if (inParentMode) {
    return <ParentCommandCenter />;
  }
  return <Dashboard />;
};

function App() {
  const { loadUser, user } = useAuthStore();
  const { syncFromUser } = useI18nStore();

  useEffect(() => {
    // Try to load user from stored token
    loadUser();
    
    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(console.error);
    }
  }, [loadUser]);
  
  // Sync language from user's account AFTER user is loaded (not from browser)
  useEffect(() => {
    if (user?.language) {
      syncFromUser(user.language);
    }
  }, [user?.language, syncFromUser]);

  return (
    <div className="app-container bg-background min-h-screen">
      <BrowserRouter>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<AuthRoute><Auth /></AuthRoute>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding" element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
            <Route path="/session/:conceptId" element={<ProtectedRoute><Session /></ProtectedRoute>} />
            <Route path="/quiz/:sessionId" element={<ProtectedRoute><Quiz /></ProtectedRoute>} />
            <Route path="/teach-back/:sessionId" element={<ProtectedRoute><TeachBack /></ProtectedRoute>} />
            <Route path="/recall" element={<ProtectedRoute><Recall /></ProtectedRoute>} />
            <Route path="/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />
            <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
            <Route path="/concept/:conceptId" element={<ProtectedRoute><ConceptView /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
            <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/premium" element={<ProtectedRoute><Premium /></ProtectedRoute>} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/certificate" element={<ProtectedRoute><Certificate /></ProtectedRoute>} />
            <Route path="/simo-exchange" element={<ProtectedRoute><SimoExchange /></ProtectedRoute>} />
            <Route path="/challenge" element={<ProtectedRoute><Challenge /></ProtectedRoute>} />
            <Route path="/parent-dashboard" element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
            <Route path="/parent-home" element={<ProtectedRoute><ParentCommandCenter /></ProtectedRoute>} />
            <Route path="/bank" element={<ProtectedRoute><ParentBank /></ProtectedRoute>} />
            <Route path="/protocols" element={<ProtectedRoute><ProtocolsPage /></ProtectedRoute>} />
            <Route path="/lab" element={<ProtectedRoute><Lab /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/cerebral-voyage" element={<ProtectedRoute><CerebralVoyage /></ProtectedRoute>} />
            <Route path="/rewards" element={<ProtectedRoute><ChildRewards /></ProtectedRoute>} />
            <Route path="/referral" element={<ProtectedRoute><Referral /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
